/**
 * PublicConversation - Simplified chat interface for public interviewees.
 * Displays AI/user message bubbles, MCQ options, and a text input.
 */

import { useState, useRef, useEffect } from 'react'

import type { AIInterviewMessage, AIInterviewProgressData } from '@ispot/shared'

import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Send, Loader2, Bot, User, LogOut } from 'lucide-react'
import { cn } from '@/lib/utils'
import { InterviewProgress } from './InterviewProgress'

// ============================================================================
// TYPES
// ============================================================================

interface PublicConversationProps {
  messages: AIInterviewMessage[]
  mcqOptions: string[]
  isStreaming: boolean
  progress: AIInterviewProgressData | null
  startedAt: string | null
  estimatedDuration: number
  onSendMessage: (content: string, inputMethod?: string, selectedMcqOption?: string) => void
  onComplete: () => void
}

// ============================================================================
// COMPONENT
// ============================================================================

export function PublicConversation({
  messages,
  mcqOptions,
  isStreaming,
  progress,
  startedAt,
  estimatedDuration,
  onSendMessage,
  onComplete,
}: PublicConversationProps) {
  const [input, setInput] = useState('')
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  // Auto-scroll on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, isStreaming])

  function handleSendText() {
    const trimmed = input.trim()
    if (!trimmed || isStreaming) return
    onSendMessage(trimmed, 'text')
    setInput('')
    textareaRef.current?.focus()
  }

  function handleSelectMcq(option: string) {
    if (isStreaming) return
    onSendMessage(option, 'mcq', option)
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSendText()
    }
  }

  return (
    <div className="flex h-full flex-col bg-warm-50">
      {/* Header */}
      <header className="flex items-center justify-between border-b border-warm-200 bg-white px-4 py-3">
        <div className="flex items-center gap-2">
          <Bot className="h-5 w-5 text-business" />
          <span className="font-display text-sm font-semibold text-warm-800">
            iSpot Interview
          </span>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={onComplete}
          className="gap-1.5 text-warm-500 hover:text-warm-700"
        >
          <LogOut className="h-4 w-4" />
          End Interview
        </Button>
      </header>

      {/* Progress bar */}
      <InterviewProgress
        progress={progress}
        startedAt={startedAt}
        estimatedDuration={estimatedDuration}
        onFinish={onComplete}
      />

      {/* Messages area */}
      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-2xl space-y-4 px-4 py-6">
          {messages.map((msg) => (
            <MessageBubble key={msg.id} message={msg} />
          ))}

          {/* Streaming indicator */}
          {isStreaming && (
            <div className="flex items-start gap-3">
              <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-business/10">
                <Bot className="h-4 w-4 text-business" />
              </div>
              <div className="rounded-2xl rounded-tl-sm bg-warm-100 px-4 py-3">
                <Loader2 className="h-4 w-4 animate-spin text-warm-400" />
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input area */}
      <div className="border-t border-warm-200 bg-white">
        <div className="mx-auto max-w-2xl px-4 py-3">
          {/* MCQ options */}
          {mcqOptions.length > 0 && !isStreaming && (
            <div className="mb-3 flex flex-wrap gap-2">
              {mcqOptions.map((option) => (
                <Button
                  key={option}
                  variant="outline"
                  size="sm"
                  onClick={() => handleSelectMcq(option)}
                  className="border-business/30 text-business hover:bg-business/5"
                >
                  {option}
                </Button>
              ))}
            </div>
          )}

          {/* Text input */}
          <div className="flex items-end gap-2">
            <Textarea
              ref={textareaRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type your response..."
              disabled={isStreaming}
              rows={1}
              className="min-h-[44px] max-h-32 resize-none border-warm-200 focus-visible:ring-business/30"
            />
            <Button
              onClick={handleSendText}
              disabled={!input.trim() || isStreaming}
              size="icon"
              className="h-11 w-11 flex-shrink-0 bg-business text-white hover:bg-business/90"
            >
              {isStreaming ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ============================================================================
// MESSAGE BUBBLE
// ============================================================================

function MessageBubble({ message }: { message: AIInterviewMessage }) {
  const isAI = message.role === 'ai'

  if (message.role === 'system') return null

  return (
    <div className={cn('flex items-start gap-3', !isAI && 'flex-row-reverse')}>
      {/* Avatar */}
      <div
        className={cn(
          'flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full',
          isAI ? 'bg-business/10' : 'bg-warm-200',
        )}
      >
        {isAI ? (
          <Bot className="h-4 w-4 text-business" />
        ) : (
          <User className="h-4 w-4 text-warm-600" />
        )}
      </div>

      {/* Bubble */}
      <div
        className={cn(
          'max-w-[80%] rounded-2xl px-4 py-3 text-sm leading-relaxed',
          isAI
            ? 'rounded-tl-sm bg-warm-100 text-warm-800'
            : 'rounded-tr-sm bg-business text-white',
        )}
      >
        {message.content}
      </div>
    </div>
  )
}
